import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBook, 
  faCheckCircle, 
  faCertificate,
  faChevronDown,
  faChevronUp,
  faLink,
  faFileAlt,
  faUserTie,
  faCalendarAlt,
  faClock,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import './StudentClasses.css';

const StudentClasses = ({  StudentId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const accordionRef = useRef(null);
    console.log(StudentId);
  useEffect(() => {
    const fetchMyClasses = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/student/studentclasses/${StudentId}`);
        setData(response.data);
        
        // Initialize all modules as collapsed
        const modulesState = {};
        response.data.myclasses.forEach(course => {
          if (!modulesState[course.ModuleName]) {
            modulesState[course.ModuleName] = false;
          }
        });
        setExpandedModules(modulesState);
      } catch (err) {
        setError(err.message || 'Failed to fetch MyClasses data');
      } finally {
        setLoading(false);
      }
    };

        if (StudentId) fetchMyClasses();
        else {
        setError('No Student ID available');
        setLoading(false);
        }
  }, [StudentId]);
  // Close accordion when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accordionRef.current && !accordionRef.current.contains(event.target)) {
        setExpandedModules(prev => {
          const newState = {};
          Object.keys(prev).forEach(module => {
            newState[module] = false;
          });
          return newState;
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleModule = (moduleName) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleName]: !prev[moduleName]
    }));
  };

  // Check if course is completed and get all completion details
  const getCourseCompletions = (courseId) => {
    if (!data?.myallclassesmade) return [];
    return data.myallclassesmade.filter(item => item.IdClass === courseId);
  };

  const getModuleProgress = (moduleName) => {
    if (!data?.myclasses || !data?.myallclassesmade) return { completed: 0, total: 0 };
    
    const moduleCourses = data.myclasses.filter(course => course.ModuleName === moduleName);
    
    // Determine the limit based on course count
    let limit;
    if (moduleCourses.length > 8 && moduleCourses.length < 16) {
      limit = 8;
    } else if (moduleCourses.length > 16 && moduleCourses.length < 24) {
      limit = 16;
    } else if (moduleCourses.length > 24) {
      limit = 24;
    } else {
      limit = moduleCourses.length;
    }
    
    const limitedCourses = moduleCourses.slice(0, limit);
    const completed = limitedCourses.filter(course => 
      data.myallclassesmade.some(item => item.IdClass === course.Id)
    ).length;
    
    return {
      completed,
      total: limit,
      percentage: Math.round((completed / limit) * 100)
    };
  };

  const isModuleFullyCompleted = (moduleName) => {
    const progress = getModuleProgress(moduleName);
    return progress.completed === progress.total;
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <FontAwesomeIcon 
        key={i} 
        icon={faStar} 
        className={i < rating ? 'star filled' : 'star'} 
      />
    ));
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: 'N/A', time: 'N/A' };
    
    const date = new Date(dateTimeString);
    const optionsDate = { year: 'numeric', month: 'short', day: 'numeric' };
    const optionsTime = { hour: '2-digit', minute: '2-digit', hour12: true };
    
    return {
      date: date.toLocaleDateString('en-US', optionsDate),
      time: date.toLocaleTimeString('en-US', optionsTime)
    };
  };

  // Group courses by module
  const coursesByModule = {};
  if (data?.myclasses) {
    data.myclasses.forEach(course => {
      if (!coursesByModule[course.ModuleName]) {
        coursesByModule[course.ModuleName] = [];
      }
      coursesByModule[course.ModuleName].push(course);
    });
  }

  if (loading) return (
    <div className="my-classes-loading-container">
      <div className="my-classes-loading-spinner"></div>
      <p>Loading your Classes...</p>
    </div>
  );

  if (error) return (
    <div className="my-classes-error-container">
      <div className="my-classes-error-message">{error}</div>
      <button className="my-classes-retry-button" onClick={() => window.location.reload()}>
        Try Again
      </button>
    </div>
  );

  if (!data) return (
    <div className="my-classes-no-data-container">
      <div className="my-classes-no-data">No data available</div>
      <p>We couldn't find any data for your account</p>
    </div>
  );

  return (
    <div className="student-classes-container" ref={accordionRef}>
      

      {/* Stats Overview */}
      <section className="student-classes-stats-section">
        <div className="student-classes-stats-overview shadow">
          <div className="student-classes-stat-card primary">
            <div className="student-classes-stat-icon">
              <FontAwesomeIcon icon={faBook} />
            </div>
            <div className="student-classes-stat-content">
              <h3>Class Count</h3>
              <p className="student-classes-stat-value">{data.nbofclasstaken}/{data.nbofclass}</p>
            </div>
          </div>
          
          <div className="student-classes-stat-card success">
            <div className="student-classes-stat-icon">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <div className="student-classes-stat-content">
              <h3>Lessons</h3>
              <p className="student-classes-stat-value">{data.nbofprojectsdone}/{data.totalprojects}</p>
            </div>
          </div>
          
          <div className="student-classes-stat-card accent">
            <div className="student-classes-stat-icon">
              <FontAwesomeIcon icon={faCertificate} />
            </div>
            <div className="student-classes-stat-content">
              <h3>Learning Path</h3>
              <p className="student-classes-stat-value">{data.percent}%</p>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Accordion */}
      <div className="student-classes-courses-accordion">
        {Object.keys(coursesByModule).map(moduleName => {
          const progress = getModuleProgress(moduleName);
          const isCompleted = isModuleFullyCompleted(moduleName);
          
          return (
            <div 
              className={`student-classes-module ${expandedModules[moduleName] ? 'expanded' : ''}`} 
              key={moduleName}
            >
              <div 
                className={`student-classes-module-header ${isCompleted ? 'completed' : progress.completed > 0 ? 'in-progress' : 'not-started'}`}
                onClick={() => toggleModule(moduleName)}
              >
                <div className={`student-classes-module-status ${isCompleted ? 'completed' : progress.completed > 0 ? 'in-progress' : 'not-started'}`}>
                  <span className="student-classes-status-label">
                    {isCompleted ? 'Completed' : progress.completed > 0 ? 'In Progress' : 'Not Started'}
                  </span>
                </div>
                
                <div className="student-classes-module-title-wrapper">
                  <h2>{moduleName}</h2>
                  <div className="student-classes-module-progress">
                    <span className="student-classes-progress-count">{progress.completed}/{progress.total}</span>
                  </div>
                </div>
                
                <div className="student-classes-progress-bar-container">
                  <div 
                    className="student-classes-progress-bar"
                    style={{ width: `${progress.percentage}%` }}
                  ></div>
                </div>
                
                <FontAwesomeIcon 
                  icon={expandedModules[moduleName] ? faChevronUp : faChevronDown} 
                  className="student-classes-toggle-icon"
                />
              </div>
              
              <div className="student-classes-module-content">
                {coursesByModule[moduleName].map((course, index) => {
                  const completions = getCourseCompletions(course.Id);
                  const hasCompletions = completions.length > 0;
                  
                  return (
                    <div 
                      className={`student-classes-course-row ${hasCompletions ? 'completed' : ''}`} 
                      key={`${course.Id}-${index}`}
                    >
                      <div className="student-classes-course-image">
                        <img 
                          src={course.CourseFilesLink} 
                          alt={course.CourseName} 
                          onError={(e) => {
                            e.target.src = '../../assets/coursesimages/206071006_a1d57977-9a9b-4428-9a06-4027494bf664-1024x576.jpg';
                          }}
                        />
                        
                      </div>
                      
                      <div className="student-classes-course-main">
                        <div className="student-classes-course-title">
                          <h3>{course.CourseName.trim()}</h3>
                        </div>

                        <div className="student-classes-course-buttons">
                          {course.CourseLink && (
                            <a 
                              href={course.CourseLink} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="student-classes-course-button"
                            >
                              <FontAwesomeIcon icon={faLink} /> Open Project
                            </a>
                          )}
                          
                          {course.CourseProjectLink && (
                            <a 
                              href={course.CourseProjectLink} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="student-classes-course-button"
                            >
                              <FontAwesomeIcon icon={faFileAlt} /> Check Project
                            </a>
                          )}
                        </div>
                      </div>
                      
                      {hasCompletions && (
                        <div className="student-classes-course-details-container">
                          {completions.map((completion, idx) => {
                            const { date, time } = formatDateTime(completion.ClassDateTime);
                            return (
                              <div className="student-classes-course-details" key={`completion-${idx}`}>
                                <div className="student-classes-detail-item">
                                  <FontAwesomeIcon icon={faUserTie} />
                                  <span>{completion.FirstName} {completion.LastName}</span>
                                </div>
                                <div className="student-classes-detail-item">
                                  <FontAwesomeIcon icon={faCalendarAlt} />
                                  <span>{date}</span>
                                </div>
                                <div className="student-classes-detail-item">
                                  <FontAwesomeIcon icon={faClock} />
                                  <span>{time}</span>
                                </div>
                                <div className="student-classes-detail-item student-classes-rating">
                                  {renderStars(completion.Rating || 0)}
                                </div>
                                {idx < completions.length - 1 && (
                                  <hr className="student-classes-completion-separator" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StudentClasses;